// Static per-state vehicle sales-tax rate + typical title/license fee, for
// the four-square deal worksheet's TTL (tax, title, license) estimate.
//
// Deliberately client-side (src/lib/, not functions/_lib/, despite the
// paddRegions.js naming convention this otherwise follows) — this table
// has no secret to protect and its only consumer is the worksheet's own
// live-recalculating Price tile, which needs to react on every keystroke
// with no network round-trip. paddRegions.js lives server-only because
// its only consumer (eia.js) is server-only; this table's situation is
// the opposite, so it belongs where its consumer actually is.
//
// Rates are each state's base statewide sales tax rate applied to vehicle
// purchases — NOT blended with local/county add-ons, which vary by
// jurisdiction within a state and aren't derivable from a 2-letter state
// code alone (the same granularity limit this app already accepted for
// EIA's PADD-region gas pricing). Doc/title/license fees are approximate,
// round-number placeholders per state, not exact schedules (those vary by
// vehicle weight/class and change yearly). Sourced once as a reasonable
// starting table, not verified against a live feed — flagged and badged
// `estimated` everywhere it's shown, same as this app's other formula-tier
// figures (resolveRefiRate, the demo valuation formulas). A handful of
// no-sales-tax states are real, not omissions: AK, DE, MT, NH, OR charge
// no state sales tax on vehicle purchases.
export const STATE_TAX = {
  AL: { rate: 2.0, fee: 275 }, AK: { rate: 0, fee: 150 }, AZ: { rate: 5.6, fee: 400 },
  AR: { rate: 6.5, fee: 300 }, CA: { rate: 7.25, fee: 450 }, CO: { rate: 2.9, fee: 325 },
  CT: { rate: 6.35, fee: 275 }, DE: { rate: 0, fee: 425 }, DC: { rate: 6.0, fee: 300 },
  FL: { rate: 6.0, fee: 400 }, GA: { rate: 7.0, fee: 350 }, HI: { rate: 4.17, fee: 300 },
  ID: { rate: 6.0, fee: 250 }, IL: { rate: 7.25, fee: 300 }, IN: { rate: 7.0, fee: 275 },
  IA: { rate: 5.0, fee: 250 }, KS: { rate: 6.5, fee: 300 }, KY: { rate: 6.0, fee: 275 },
  LA: { rate: 4.45, fee: 300 }, ME: { rate: 5.5, fee: 250 }, MD: { rate: 6.0, fee: 325 },
  MA: { rate: 6.25, fee: 300 }, MI: { rate: 6.0, fee: 275 }, MN: { rate: 6.875, fee: 325 },
  MS: { rate: 5.0, fee: 300 }, MO: { rate: 4.225, fee: 300 }, MT: { rate: 0, fee: 200 },
  NE: { rate: 5.5, fee: 300 }, NV: { rate: 6.85, fee: 400 }, NH: { rate: 0, fee: 250 },
  NJ: { rate: 6.625, fee: 350 }, NM: { rate: 4.0, fee: 300 }, NY: { rate: 4.0, fee: 400 },
  NC: { rate: 3.0, fee: 275 }, ND: { rate: 5.0, fee: 250 }, OH: { rate: 5.75, fee: 300 },
  OK: { rate: 4.5, fee: 325 }, OR: { rate: 0, fee: 275 }, PA: { rate: 6.0, fee: 300 },
  RI: { rate: 7.0, fee: 300 }, SC: { rate: 5.0, fee: 400 }, SD: { rate: 4.0, fee: 250 },
  TN: { rate: 7.0, fee: 300 }, TX: { rate: 6.25, fee: 425 }, UT: { rate: 6.1, fee: 300 },
  VT: { rate: 6.0, fee: 250 }, VA: { rate: 4.15, fee: 300 }, WA: { rate: 6.5, fee: 450 },
  WV: { rate: 6.0, fee: 275 }, WI: { rate: 5.0, fee: 275 }, WY: { rate: 4.0, fee: 250 },
};

const NATIONAL_AVERAGE = { rate: 5.3, fee: 325 };

// Falls back to a rough national-average estimate for a household with
// no state on file — never a bare 0, same graceful-degradation convention
// as every other resolver in this app.
export function getStateTax(stateCode) {
  return STATE_TAX[String(stateCode || "").toUpperCase()] || NATIONAL_AVERAGE;
}

export function estimateTTL(taxableAmount, stateCode) {
  const { rate, fee } = getStateTax(stateCode);
  const tax = Math.round((Math.max(0, taxableAmount) * rate) / 100);
  return { tax, fee, total: tax + fee, rate };
}
